(function () {
  'use strict';
  const KEY = 'dsrcv:vestico-cart-drawer:v1';
  const TTL = 120000;
  const TRIGGER = '[data-action="open-drawer"][data-drawer-id="sidebar-cart"]';
  const CTA = '[class*="MainWindow-module-root"] [class*="SizeRecommendationTabSizeRec-module-btnWrapper"] button';

  function createVesticoCartDrawer(win, options = {}) {
    const doc = win.document;
    const productId = String(options.productId || '');
    const now = options.now || Date.now;
    const fetchCart = options.fetch || win.fetch?.bind(win);
    const setTimer = options.setTimeout || win.setTimeout.bind(win);
    const clearTimer = options.clearTimeout || win.clearTimeout.bind(win);
    let storage;
    try { storage = options.storage || win.sessionStorage; } catch (_) {}
    if (!storage || !fetchCart || !/^[1-9]\d*$/.test(productId)) return { destroy() {} };
    let cartURL;
    try {
      cartURL = new URL(options.cartUrl || '/cart.js', win.location.origin);
      if (cartURL.origin !== win.location.origin || !cartURL.pathname.endsWith('/cart.js')) return { destroy() {} };
    } catch (_) { return { destroy() {} }; }
    const path = win.location.pathname.replace(/\/+$/, '') || '/';
    const counts = lines => {
      if (!Array.isArray(lines)) throw new Error('Invalid cart lines');
      const result = Object.create(null);
      lines.forEach(line => {
        const id = String(line.id);
        if (!/^[1-9]\d*$/.test(id) || !Number.isSafeInteger(line.quantity) || line.quantity < 0) throw new Error('Invalid cart quantity');
        result[id] = (result[id] || 0) + line.quantity;
      });
      return result;
    };
    let serverCounts;
    try { serverCounts = counts(options.initialLines || []); } catch (_) { return { destroy() {} }; }
    let baseline = serverCounts, generation = 0, armed = false, destroyed = false, suspended = false, checking = false, pendingOpen = null;
    const requests = new Set();
    const removeIntent = () => { try { storage.removeItem(KEY); } catch (_) {} };
    const getIntent = () => {
      let value;
      try {
        const raw = storage.getItem(KEY);
        if (!raw) return null;
        if (raw.length > 12000) throw new Error('Oversized cart intent');
        value = JSON.parse(raw);
        const age = now() - value.at;
        if (value.version !== 1 || value.productId !== productId || value.path !== path || !Number.isFinite(age) || age < 0 || age > TTL) throw new Error('Expired cart intent');
        if (!value.before || typeof value.before !== 'object' || Array.isArray(value.before)) throw new Error('Invalid cart intent');
        Object.entries(value.before).forEach(([id, quantity]) => {
          if (!/^[1-9]\d*$/.test(id) || !Number.isSafeInteger(quantity) || quantity < 0) throw new Error('Invalid cart intent');
        });
        return value;
      } catch (_) { removeIntent(); return null; }
    };
    const readCart = async () => {
      const controller = win.AbortController ? new win.AbortController() : null;
      let rejectStop;
      const stopped = new Promise((_, reject) => { rejectStop = reject; });
      const cancel = () => { controller?.abort(); rejectStop(new Error('Cart read cancelled')); };
      requests.add(cancel);
      const deadline = setTimer(cancel, 5000);
      try {
        return await Promise.race([stopped, (async () => {
          const response = await fetchCart(cartURL.pathname, { credentials: 'same-origin', cache: 'no-store', headers: { Accept: 'application/json' }, ...(controller ? { signal: controller.signal } : {}) });
          if (!response.ok) throw new Error('Cart read failed');
          const cart = await response.json();
          if (!Array.isArray(cart.items)) throw new Error('Invalid cart');
          return counts(cart.items.filter(item => String(item.product_id) === productId).map(item => ({ id: item.variant_id, quantity: item.quantity })));
        })()]);
      } finally { clearTimer(deadline); requests.delete(cancel); }
    };
    const openReady = () => {
      if (!pendingOpen || destroyed || suspended || doc.hidden) return;
      if (now() - pendingOpen.at > TTL) { pendingOpen = null; return; }
      const drawer = doc.getElementById('sidebar-cart');
      if (!drawer?.__dsrcvDrawerReady) return;
      const trigger = Array.from(doc.querySelectorAll(TRIGGER)).find(element => element.getClientRects().length);
      if (!trigger) return;
      pendingOpen = null;
      if (drawer.getAttribute('aria-hidden') !== 'false') trigger.click();
    };
    const resume = async () => {
      openReady();
      if (destroyed || suspended || checking || doc.hidden) return;
      const intent = getIntent();
      if (!intent) return;
      // Consume before awaiting so repeated lifecycle events cannot reopen it.
      removeIntent(); checking = true;
      const current = generation;
      try {
        const live = await readCart();
        if (destroyed || current !== generation) return;
        const total = value => Object.values(value).reduce((sum, quantity) => sum + quantity, 0);
        const added = total(live) > total(intent.before) && total(serverCounts) > total(intent.before) &&
          Object.keys(live).some(id => live[id] > (intent.before[id] || 0) && (serverCounts[id] || 0) > (intent.before[id] || 0));
        if (added) { pendingOpen = intent; openReady(); }
      } catch (_) {
        // The provider owns the add; never retry a cart write on uncertain state.
      } finally { checking = false; }
    };
    const onOpen = () => {
      const current = ++generation;
      armed = false;
      baseline = serverCounts;
      readCart().then(value => { if (!destroyed && generation === current && !armed) baseline = value; }).catch(() => {});
    };
    const onClose = () => {
      generation++;
      if (armed) removeIntent();
      armed = false;
    };
    const onClick = event => {
      const button = event.target?.closest?.(CTA);
      if (!button || button.disabled || destroyed) return;
      armed = true;
      try { storage.setItem(KEY, JSON.stringify({ version: 1, productId, path, at: now(), before: baseline })); } catch (_) {}
    };
    const onHide = () => {
      suspended = true;
      generation++;
      pendingOpen = null;
      requests.forEach(cancel => cancel());
    };
    const onShow = event => {
      if (event.persisted) { onHide(); suspended = false; removeIntent(); armed = false; return; }
      suspended = false;
      resume();
    };
    const onReady = event => { if (event.target.id === 'sidebar-cart') openReady(); };
    doc.addEventListener('vw-open', onOpen, true);
    doc.addEventListener('vw-close', onClose, true);
    doc.addEventListener('click', onClick, true);
    doc.addEventListener('theme:cart-drawer-ready', onReady, true);
    doc.addEventListener('visibilitychange', resume);
    win.addEventListener('pagehide', onHide);
    win.addEventListener('pageshow', onShow);
    if (doc.readyState === 'loading') doc.addEventListener('DOMContentLoaded', resume, { once: true });
    else resume();
    return { destroy() {
      destroyed = true; onHide();
      doc.removeEventListener('vw-open', onOpen, true);
      doc.removeEventListener('vw-close', onClose, true);
      doc.removeEventListener('click', onClick, true);
      doc.removeEventListener('theme:cart-drawer-ready', onReady, true);
      doc.removeEventListener('visibilitychange', resume);
      doc.removeEventListener('DOMContentLoaded', resume);
      win.removeEventListener('pagehide', onHide);
      win.removeEventListener('pageshow', onShow);
    } };
  }
  if (typeof module !== 'undefined' && module.exports) module.exports = { createVesticoCartDrawer, KEY };
  if (typeof window !== 'undefined' && document.currentScript?.dataset.vesticoCartProduct && !window.__dsrcvVesticoCartDrawer) {
    const script = document.currentScript;
    let initialLines;
    try { initialLines = JSON.parse(script.dataset.vesticoCartBaseline || '[]'); } catch (_) { initialLines = null; }
    if (initialLines) window.__dsrcvVesticoCartDrawer = createVesticoCartDrawer(window, { productId: script.dataset.vesticoCartProduct, cartUrl: script.dataset.vesticoCartUrl, initialLines });
  }
}());
